import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { psGet, psPost } from "./PSApiClient";
import { ui, PrimaryButton, GhostButton, ErrorState, StatusPill } from "./psShared";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function PSIncome() {
    // const user = useSelector((s) => s.authState?.user);
    const authState = useSelector(state => state.authReducer);
    const user = authState?.user;

  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogForm, setShowLogForm] = useState(false);
  const [form, setForm] = useState({ earnings: "", source: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const now = new Date();

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, h, p] = await Promise.all([
        psGet("/income/summary"),
        psGet("/income/me"),
        psGet("/profiles/me"),
      ]);
      setSummary(s);
      setHistory(h || []);
      setProfile(p);
    } catch (e) {
      // FIX: if this still shows 404 after these files are in place, the
      // backend process almost certainly needs a restart — /income/summary,
      // /income/me and /profiles/me are all real routes in the psRoutes.js
      // already handed over. A stale running process is the #1 cause of a
      // route "existing in the file" but still 404ing.
      setError({ status: e.status, message: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleLogIncome = async (e) => {
    e.preventDefault();
    if (!form.earnings) return;
    setError(null);
    try {
      setSaving(true);
      await psPost("/income", {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        earnings: Number(form.earnings),
        source: form.source,
      });
      setForm({ earnings: "", source: "" });
      setShowLogForm(false);
      await loadAll();
    } catch (e) {
      setError({ status: e.status, message: e.message });
    } finally {
      setSaving(false);
    }
  };

  const chartData = {
    labels: history.map((e) => `${MONTH_LABELS[e.month - 1]} '${String(e.year).slice(2)}`),
    datasets: [
      {
        label: "Earnings",
        data: history.map((e) => e.earnings),
        backgroundColor: "#C084FC",
        borderRadius: 8,
        maxBarThickness: 36,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: "rgba(255,255,255,0.5)", font: { size: 11 } } },
      y: {
        grid: { color: "rgba(255,255,255,0.08)" },
        ticks: { color: "rgba(255,255,255,0.5)", font: { size: 10 }, callback: (v) => `₹${v}` },
      },
    },
  };

  const membershipStatus = profile?.membership?.status || "inactive";
  const isFoundHerAlum = profile?.foundherAlumni;

  const statusBadge = isFoundHerAlum
    ? { tone: "role", label: "FoundHer • Free Access" }
    : membershipStatus === "active"
    ? { tone: "live", label: "Membership Active" }
    : membershipStatus === "grace"
    ? { tone: "pending", label: "Renew Soon" }
    : { tone: "danger", label: "Membership Inactive" };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] lg:text-sm text-white/45">Welcome back,</p>
            <h1
              className="text-lg font-bold text-white sm:text-2xl"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {user?.name || "Entrepreneur"} 👋
            </h1>
          </div>
          <StatusPill tone={statusBadge.tone}>{statusBadge.label}</StatusPill>
        </div>

        {error && (
          <div className="mt-4">
            <ErrorState
              message={
                error.status === 404
                  ? "Couldn't reach the dashboard data — the server may need a restart."
                  : error.message
              }
              onRetry={loadAll}
            />
          </div>
        )}

        {/* Hero stat */}
        <div className={`${ui.card} mt-6 overflow-hidden bg-gradient-to-br from-violet-600/30 via-fuchsia-600/20 to-rose-500/20 p-6 sm:p-8`}>
          <p className="text-[11px] lg:text-sm font-medium text-white/60">Total earnings logged</p>
          <p
            className="mt-1 text-3xl font-extrabold tracking-tight text-white sm:text-5xl"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {loading ? "…" : `₹${(summary?.total || 0).toLocaleString("en-IN")}`}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <div className="rounded-2xl bg-white/10 px-4 py-2">
              <p className="text-[10px] lg:text-xs text-white/55">This month</p>
              <p className="text-base lg:text-lg font-bold text-white">₹{(summary?.thisMonthTotal || 0).toLocaleString("en-IN")}</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-2">
              <p className="text-[10px] lg:text-xs text-white/55">Avg / entry</p>
              <p className="text-base lg:text-lg font-bold text-white">₹{(summary?.avgMonthly || 0).toLocaleString("en-IN")}</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-2">
              <p className="text-[10px] lg:text-xs text-white/55">Entries</p>
              <p className="text-base lg:text-lg font-bold text-white">{summary?.entriesCount || 0}</p>
            </div>
          </div>
        </div>

        <PrimaryButton className="mt-4" onClick={() => setShowLogForm(true)}>
          <FontAwesomeIcon icon={faPlus} className="mr-1.5 text-[10px]" />
          Log this month's earnings
        </PrimaryButton>

        {/* Chart */}
        <div className={`${ui.card} mt-6 p-5 sm:p-6`}>
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] lg:text-base font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Earnings trend
            </h2>
            <span className="text-[10px] lg:text-xs text-white/40">Last {history.length} entries</span>
          </div>
          <div className="mt-4 h-56 sm:h-72">
            {loading ? (
              <div className="flex h-full items-center justify-center text-[12px] text-white/40">Loading chart…</div>
            ) : history.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-1 text-center text-[12px] text-white/40">
                <p>No earnings logged yet.</p>
                <p>Tap "Log this month's earnings" to start your trend.</p>
              </div>
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Profile snapshot */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Skill" value={profile?.skill || "—"} />
          <StatCard label="Jobs done" value={profile?.jobsDone ?? "—"} />
          <StatCard label="Rating" value={profile?.rating ? `${profile.rating}★` : "—"} />
          <StatCard label="City" value={profile?.userId?.city || "—"} />
        </div>
      </div>

      {/* Log income bottom sheet / modal — dark, matches the rest of the app now */}
      {showLogForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
          <form
            onSubmit={handleLogIncome}
            className="w-full max-w-md rounded-t-3xl border border-white/10 bg-[#171225] p-6 shadow-xl sm:rounded-3xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Log this month's earnings
                </h3>
                <p className="mt-1 text-[12px] text-white/45">
                  {MONTH_LABELS[now.getMonth()]} {now.getFullYear()}
                </p>
              </div>
              <button type="button" onClick={() => setShowLogForm(false)} className="w-fit px-2 py-1 text-white/40 hover:text-white/70">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <label className={`${ui.label} mt-4`}>Amount (₹)</label>
            <input
              type="number"
              min="0"
              required
              value={form.earnings}
              onChange={(e) => setForm({ ...form, earnings: e.target.value })}
              className={`${ui.input} mt-1 text-lg font-semibold`}
              placeholder="e.g. 8000"
            />

            <label className={`${ui.label} mt-4`}>Source (optional)</label>
            <input
              type="text"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className={`${ui.input} mt-1`}
              placeholder="e.g. Wiring job - Kothrud"
            />

            <div className="mt-6 flex gap-3">
              <GhostButton type="button" className="flex-1 justify-center" onClick={() => setShowLogForm(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton type="submit" disabled={saving} className="flex-1 justify-center">
                {saving ? "Saving…" : "Save"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className={`${ui.card} p-3.5`}>
      <p className="text-[10px] lg:text-xs text-white/45">{label}</p>
      <p className="mt-1 truncate text-[13px] lg:text-base font-bold text-white">{value}</p>
    </div>
  );
}
